import { Button, Icon, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { crmWebhookLogs } from "../../api/crm-webhook-logs.service.js";
import { PageHeader } from "../../components/PageHeader/index.js";
import { UnauthorizedView } from "../../components/UnauthorizedView/index.js";
import { ROUTES } from "../../routes.js";
import styles from "./CrmWebhookLogs.module.css";

function CrmWebhookLogsLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [authError, setAuthError] = useState<Error | null>(null);
  const [_authCheckLoading, setAuthCheckLoading] = useState(true);
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
  } | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    async function checkAuth() {
      try {
        await crmWebhookLogs.getAll({ limit: 1, offset: 0 });
        setAuthCheckLoading(false);
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to check auth");
        const status = (error as Error & { status?: number }).status;
        const isAuthError = status === 401 || status === 403;

        if (isAuthError) {
          setAuthError(error);
        }
        setAuthCheckLoading(false);
      }
    }
    checkAuth();
  }, []);

  function handleCloseToast() {
    setToast(null);
  }

  function handleMutationSuccess() {
    setRefetchTrigger(prev => prev + 1);
  }

  function handleBackClick() {
    if (window.history.length > 1 && location.key !== "default") {
      navigate(-1);
    } else {
      navigate(ROUTES.fulfilment.crmWebhookLogs);
    }
  }

  const isDetailPage = location.pathname !== ROUTES.fulfilment.crmWebhookLogs;

  if (authError) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader title="CRM Webhook" subtitle="View and search CRM webhook call logs">
          <div style={{ display: "flex", gap: "12px" }}>
            {isDetailPage && (
              <Button variant="outlined" color="default" onClick={handleBackClick}>
                <Icon icon="arrow-left" size="small" />
                Back
              </Button>
            )}
          </div>
        </PageHeader>
      </div>

      <Outlet
        context={{
          toast,
          setToast,
          refetchTrigger,
          onMutationSuccess: handleMutationSuccess,
        }}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={handleCloseToast}
          duration={5000}
        />
      )}
    </div>
  );
}

export default CrmWebhookLogsLayout;
